import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {NgClass} from "@angular/common";
import {PasteCleanupSettingsModel, RichTextEditorAllModule, ToolbarSettingsModel} from "@syncfusion/ej2-angular-richtexteditor";
import {ActionBeginEventArgs, PasteCleanupArgs, QuickToolbarSettingsModel} from '@syncfusion/ej2-richtexteditor';
import {LanguageService} from "../../services/language.service";
import {LocaleService} from '../../services/locale.service';
import {RichTextLinkPolicy, RichTextPastePolicy, RichTextSanitizerService} from '../../services/rich-text-sanitizer.service';

@Component({
    selector: 'app-form-textarea',
    imports: [
        NgClass,
        ReactiveFormsModule,
        RichTextEditorAllModule
    ],
    templateUrl: './form-textarea.component.html',
    styleUrl: './form-textarea.component.scss',
})
export class FormTextareaComponent implements OnInit, OnChanges, OnDestroy {
  private languageService: LanguageService = inject(LanguageService);
  private localeService: LocaleService = inject(LocaleService);
  private richTextSanitizer: RichTextSanitizerService = inject(RichTextSanitizerService);
  private richTextPasteCaptureHandler: ((event: ClipboardEvent) => void) | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('textAreaElement') public textAreaElement: any;

  @Input() public placeholder: string = '';
  @Input() public fCN: string = '';
  @Input() public class: string = '';
  @Input() public fG: FormGroup = new FormGroup({});
  @Input() public errorText: string = '';
  @Input() public style: 'inline' | 'block' | 'headless' | 'chat' = 'headless';
  @Input() public disabled: boolean = false;
  @Input() public showCharCounter: boolean = true;
  @Input() public rows: number = 3;
  @Input() public cols: number = 35;
  @Input() public maxChars: number = 0;
  @Input() public height: number = 200;
  @Input() public enableRichTextResize: boolean = false;
  @Input() public minHeight: number = 150;
  @Input() public maxHeight: number = 600;
  @Input() public richText: boolean = false;
  @Input() public allowedToolbarItems: string[] = [];
  @Input() public setEmoji: string = '';
  @Input() public pastePolicy: RichTextPastePolicy = 'balanced';
  @Input() public linkPolicy: RichTextLinkPolicy = 'strip';
  @Output() public blurred: EventEmitter<void> = new EventEmitter<void>();
  @Output() public returnKeyPress: EventEmitter<void> = new EventEmitter<void>();
  @Output() public documentsUpload: EventEmitter<void> = new EventEmitter<void>();
  @Output() public setEmojiChange: EventEmitter<string> = new EventEmitter<string>();

  public pasteCleanupSettings: PasteCleanupSettingsModel = {
    prompt: false,
    plainText: false,
    keepFormat: true,
    deniedTags: [],
    deniedAttrs: [],
    allowedStyleProps: ['text-align', 'text-decoration', 'font-weight', 'font-style']
  };
  public toolbarSettings: ToolbarSettingsModel = {} as ToolbarSettingsModel;
  public quickToolbarSettings: QuickToolbarSettingsModel = {
    link: []
  };

  public get syncfusionLocale(): string {
    return this.localeService.getLocale();
  }

  private richTextShortcutCaptureHandler: ((event: KeyboardEvent) => void) | null = null;

  private addRichTextShortcutCaptureListener(): void {
    if (!this.richText || this.richTextShortcutCaptureHandler) {
      return;
    }

    const hostElement = this.textAreaElement?.element as HTMLElement | undefined;
    if (!hostElement) {
      return;
    }

    this.richTextShortcutCaptureHandler = (event: KeyboardEvent): void => {
      const key = event.key.toLowerCase();

      const blockedShortcut =
        (event.ctrlKey || event.metaKey) &&
        key === 'k';

      if (blockedShortcut) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    hostElement.addEventListener('keydown', this.richTextShortcutCaptureHandler, {
      capture: true
    });
  }

  public ngOnInit(): void {
    this.updateToolbarSettings();

    this.setDisabledState(this.disabled);
    this.normalizePlainTextControlValue();

    this.fG.controls[this.fCN].valueChanges.subscribe(value => {
      if (!this.richText) {
        const preparedValue = this.getPreparedPlainTextValue(value);
        if (preparedValue !== value) {
          this.fG.controls[this.fCN].setValue(preparedValue, {emitEvent: false});
          this.adjustHeight();
          return;
        }

        if (value === null) {
          if (this.textAreaElement?.nativeElement) {
            this.textAreaElement.nativeElement.style.height = 'auto';
          }
        } else {
          this.adjustHeight();
        }
      } else {
        const preparedValue = this.getPreparedRichTextValue(value);
        if (preparedValue !== value) {
          this.fG.controls[this.fCN].setValue(preparedValue, {emitEvent: false});
          if (this.textAreaElement && this.textAreaElement.value !== preparedValue) {
            this.textAreaElement.value = preparedValue;
          }
          return;
        }
      }

      if (value === '<p><br/></p>' || value === '<p><br></p>') {
        this.fG.controls[this.fCN].setValue('');
        return;
      }

    });
  }

  public onRichTextEditorCreated(): void {
    this.updateToolbarSettings();
    this.disableRichTextAutoUrl();
    this.addRichTextPasteCaptureListener();
    this.addRichTextShortcutCaptureListener();
    requestAnimationFrame(() => this.addRichTextShortcutCaptureListener());
    requestAnimationFrame(() => this.disableRichTextAutoUrl());
    requestAnimationFrame(() => this.addRichTextPasteCaptureListener());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.setDisabledState(changes['disabled'].currentValue);
    }
    if (changes['setEmoji'] && changes['setEmoji'].currentValue && !this.richText) {
      if (!this.textAreaElement?.nativeElement) {
        return;
      }
      const startPos = this.textAreaElement.nativeElement.selectionStart;
      const endPos = this.textAreaElement.nativeElement.selectionEnd;
      const text = this.textAreaElement.nativeElement.value;
      this.textAreaElement.nativeElement.value = text.slice(0, startPos) + changes['setEmoji'].currentValue + text.slice(endPos);
      const newCursorPos = startPos + changes['setEmoji'].currentValue.length;
      this.textAreaElement.nativeElement.setSelectionRange(newCursorPos, newCursorPos);
      this.textAreaElement.nativeElement.focus();
    }
  }

  private setDisabledState(disabled: boolean): void {
    const control = this.fG.controls[this.fCN];
    if (!control) return;
    if (disabled) {
      control.disable();
    } else {
      control.enable();
    }
  }

  public onReturnKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) {
        return;
      }
      if (this.style === 'chat') {
        event.preventDefault();
      }
      this.returnKeyPress.emit();
    }
  }

  public onPaste(event: ClipboardEvent): void {
    if (this.richText) {
      return;
    }

    this.handlePlainTextPaste(event);
  }

  public onBeforePasteCleanup(args: PasteCleanupArgs): void {
    if (!this.richText) {
      return;
    }

    const rawHtml = args.value ?? '';
    if (!rawHtml) {
      return;
    }

    if (this.pastePolicy === 'plain') {
      let plainValue = this.richTextSanitizer.toPlainText(rawHtml);
      if (this.maxChars) {
        const remaining = this.getRemainingTextLengthForPaste();
        plainValue = plainValue.slice(0, Math.max(0, remaining));
      }
      args.value = plainValue;
      return;
    }

    let sanitizedHtml = this.richTextSanitizer.sanitizeRichHtml(rawHtml, this.pastePolicy, this.linkPolicy);
    if (this.maxChars) {
      const remaining = this.getRemainingTextLengthForPaste();
      if (remaining <= 0) {
        args.value = '';
        return;
      }
      const sanitizedTextLength = this.richTextSanitizer.toPlainText(sanitizedHtml).length;
      if (sanitizedTextLength > remaining) {
        sanitizedHtml = this.richTextSanitizer.truncateHtmlByTextLength(sanitizedHtml, remaining);
      }
    }
    args.value = sanitizedHtml;
  }

  public onRichTextClipboard(event: ClipboardEvent, isCut: boolean = false): void {
    if (!this.richText) {
      return;
    }

    const selectionHtml = this.getSelectionHtml();
    if (!selectionHtml) {
      return;
    }

    const sanitizedHtml = this.richTextSanitizer.sanitizeRichHtml(selectionHtml, this.pastePolicy, this.linkPolicy);
    const plainText = this.richTextSanitizer.toPlainText(sanitizedHtml);

    if (!event.clipboardData) {
      return;
    }

    event.preventDefault();
    event.clipboardData.setData('text/plain', plainText);
    event.clipboardData.setData('text/html', sanitizedHtml);

    if (isCut) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        selection.deleteFromDocument();
      }
    }
  }

  
  public onRichTextActionBegin(args: ActionBeginEventArgs): void {
    if (!this.richText) {
      return;
    }

    // Syncfusion auto-url behavior causes caret jump
    // when URL text is followed by Space/Enter.
    if (args.requestType === 'CreateLink' && args.selectType === undefined) {
      args.cancel = true;
      return;
    }
  }

  public toolbarClicked(): void {
    if (this.allowedToolbarItems.includes('Image')) {
      this.documentsUpload.emit();
    } else {
      this.textAreaElement!.focusIn();
      const selection = document.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      let parentNode = range.commonAncestorContainer as HTMLElement;
      if (parentNode.nodeType === Node.TEXT_NODE) {
        parentNode = parentNode.parentElement!;
      }

      if (parentNode.closest('h5')) {
        this.textAreaElement.executeCommand('formatBlock', 'P');
      } else {
        this.textAreaElement.executeCommand('formatBlock', 'H5');
      }
    }
  }

  public adjustHeight(): void {
    if (this.richText || !this.textAreaElement?.nativeElement) {
      return;
    }
    this.textAreaElement.nativeElement.style.height = 'auto';
    this.textAreaElement.nativeElement.style.height = Math.min(this.textAreaElement.nativeElement.scrollHeight, 150) + 'px';
  }

  private updateToolbarSettings(): void {
    if (this.allowedToolbarItems.length > 0) {
      this.toolbarSettings = {
        ...this.toolbarSettings,
        items: [
          ...(this.allowedToolbarItems.includes('EmojiPicker') ? ['EmojiPicker'] : []),
          ...(this.allowedToolbarItems.includes('Image') ? [{
            tooltipText: this.languageService.getTranslationByKey('RICHTEXTSELECTIMAGETOOLTIP'),
            undo: false,
            click: this.toolbarClicked.bind(this),
            template: '<button type="button" class="e-tbar-btn e-btn e-lib e-icon-btn" tabindex="-1">'
              + '<span class="mts-icon fs-4 text-primary">photo_camera</span></button>'
          }] : [])
        ]
      };
    } else {
      this.toolbarSettings = {
        ...this.toolbarSettings,
        items: [
          'EmojiPicker',
          'Bold',
          'Italic',
          'Underline',
          {
            tooltipText: this.languageService.getTranslationByKey('RICHTEXTTITLETOOLTIP'),
            undo: true,
            click: this.toolbarClicked.bind(this),
            template:
              '<button type="button" class="e-tbar-btn e-btn e-lib e-icon-btn" tabindex="-1">'
              + '<span class="e-btn-icon e-caption e-icons"></span></button>'
          },
          '|',
          'Alignments',
          'OrderedList',
          'UnorderedList',
          '|',
          'ClearFormat'
        ]
      };
    }

    if (this.richText && this.textAreaElement) {
      this.textAreaElement.toolbarSettings = this.toolbarSettings;
      this.textAreaElement.dataBind?.();
    }
  }

  private normalizePlainTextControlValue(): void {
    if (this.richText) {
      return;
    }

    const control = this.fG.controls[this.fCN];
    const preparedValue = this.getPreparedPlainTextValue(control?.value);
    if (preparedValue !== control?.value) {
      control.setValue(preparedValue, {emitEvent: false});
    }
  }

  private addRichTextPasteCaptureListener(): void {
    if (!this.richText || this.richTextPasteCaptureHandler) {
      return;
    }

    const hostElement = this.textAreaElement?.element as HTMLElement | undefined;
    if (!hostElement) {
      return;
    }

    this.richTextPasteCaptureHandler = (event: ClipboardEvent): void => {
      const plainText = this.getClipboardPlainText(event);
      if (!this.containsUrlText(plainText)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const textToInsert = this.getLimitedRichTextPasteText(plainText);
      if (textToInsert) {
        this.textAreaElement?.executeCommand('insertText', textToInsert);
      }
    };

    hostElement.addEventListener('paste', this.richTextPasteCaptureHandler, {capture: true});
  }

  private getPreparedPlainTextValue(value: unknown): unknown {
    const normalizedValue = this.getNormalizedPlainTextValue(value);
    if (this.maxChars && typeof normalizedValue === 'string' && normalizedValue.length > this.maxChars) {
      return normalizedValue.substring(0, this.maxChars);
    }

    return normalizedValue;
  }

  private getNormalizedPlainTextValue(value: unknown): unknown {
    if (this.richText || typeof value !== 'string' || !this.containsHtmlElementMarkup(value)) {
      return value;
    }

    return this.richTextSanitizer.toPlainText(value);
  }

  private containsHtmlElementMarkup(value: string): boolean {
    return /<\s*\/?\s*[a-z][a-z0-9-]*(?:\s+[^<>]*)?\/?\s*>/i.test(value);
  }

  private getRemainingTextLengthForPaste(): number {
    if (!this.maxChars) {
      return Number.MAX_SAFE_INTEGER;
    }
    const currentValue = this.fG.controls[this.fCN]?.value ?? '';
    const currentLength = this.getCurrentTextLength(currentValue);
    const selectedLength = this.getSelectedTextLength();
    return this.maxChars - (currentLength - selectedLength);
  }

  private handlePlainTextPaste(event: ClipboardEvent): void {
    if (this.hasClipboardFiles(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    let finalText =
      event.clipboardData?.getData('text/plain') ??
      event.clipboardData?.getData('text') ??
      '';

    if (!finalText) {
      const clipboardHtml = event.clipboardData?.getData('text/html') ?? '';
      finalText = clipboardHtml
        ? this.richTextSanitizer.toPlainText(clipboardHtml)
        : '';
    }

    if (!finalText) {
      return;
    }

    if (this.maxChars) {
      const currentValue = this.fG.controls[this.fCN]?.value ?? '';
      const currentLength = this.getCurrentTextLength(currentValue);
      const selectedLength = this.getSelectedTextLength();
      const remainingLength = this.maxChars - (currentLength - selectedLength);

      if (remainingLength <= 0) {
        return;
      }

      finalText = finalText.slice(0, remainingLength);
    }

    this.insertPlainTextIntoTextarea(finalText);
  }

  private hasClipboardFiles(event: ClipboardEvent): boolean {
    if (event.clipboardData?.types.includes('Files')) {
      return true;
    }

    return Array.from(event.clipboardData?.items ?? []).some(item => item.kind === 'file');
  }

  private insertPlainTextIntoTextarea(textToInsert: string): void {
    if (!this.textAreaElement?.nativeElement) {
      return;
    }

    const nativeElement = this.textAreaElement.nativeElement as HTMLTextAreaElement;
    const startPos = nativeElement.selectionStart ?? nativeElement.value.length;
    const endPos = nativeElement.selectionEnd ?? nativeElement.value.length;

    const currentValue = this.fG.controls[this.fCN]?.value ?? '';
    const updatedText =
      currentValue.slice(0, startPos) +
      textToInsert +
      currentValue.slice(endPos);

    this.fG.controls[this.fCN].setValue(updatedText);

    requestAnimationFrame(() => {
      nativeElement.setSelectionRange(
        startPos + textToInsert.length,
        startPos + textToInsert.length
      );
    });
  }

  private getClipboardPlainText(event: ClipboardEvent): string {
    return event.clipboardData?.getData('text/plain') ??
      event.clipboardData?.getData('text') ??
      '';
  }

  private getLimitedRichTextPasteText(text: string): string {
    if (!this.maxChars) {
      return text;
    }

    const remainingLength = this.getRemainingTextLengthForPaste();
    if (remainingLength <= 0) {
      return '';
    }

    return text.slice(0, remainingLength);
  }

  private containsUrlText(value: string): boolean {
    return /(?:https?:\/\/|www\.)[^\s<>]+/i.test(value);
  }

  private getSelectionHtml(): string {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return '';
    }

    const range = selection.getRangeAt(0);
    const container = document.createElement('div');
    container.appendChild(range.cloneContents());
    return container.innerHTML;
  }

  private getCurrentTextLength(value: string | null): number {
    if (!value) {
      return 0;
    }

    if (!this.richText) {
      return value.length;
    }

    return this.richTextSanitizer.toPlainText(value).length;
  }

  private getSelectedTextLength(): number {
    if (!this.richText && this.textAreaElement?.nativeElement) {
      const nativeElement = this.textAreaElement.nativeElement as HTMLTextAreaElement;
      return Math.max(0, (nativeElement.selectionEnd ?? 0) - (nativeElement.selectionStart ?? 0));
    }

    const selection = document.getSelection();
    return selection ? selection.toString().length : 0;
  }

  private getPreparedRichTextValue(value: unknown): unknown {
    if (typeof value !== 'string') {
      return value;
    }

    let preparedValue = value;

    if (this.containsAnchorMarkup(preparedValue)) {
      preparedValue = this.richTextSanitizer.sanitizeRichHtml(preparedValue, this.pastePolicy, 'strip');
    }

    if (this.maxChars) {
      const currentTextLength = this.richTextSanitizer.toPlainText(`${preparedValue}`).length;
      if (currentTextLength > this.maxChars) {
        preparedValue = this.richTextSanitizer.truncateHtmlByTextLength(preparedValue, this.maxChars);
      }
    }

    return preparedValue;
  }

  private containsAnchorMarkup(value: string): boolean {
    return /<\s*a(?:\s|>)/i.test(value);
  }

  private disableRichTextAutoUrl(): void {
    if (!this.richText || !this.textAreaElement) {
      return;
    }

    this.textAreaElement.enableAutoUrl = false;
    this.textAreaElement.quickToolbarSettings = {
      ...this.textAreaElement.quickToolbarSettings,
      link: []
    };
    this.textAreaElement.dataBind?.();
  }

  public ngOnDestroy(): void {
    const hostElement = this.textAreaElement?.element as HTMLElement | undefined;
    if (hostElement && this.richTextPasteCaptureHandler) {
      hostElement.removeEventListener('paste', this.richTextPasteCaptureHandler, {capture: true});
      this.richTextPasteCaptureHandler = null;
    }

    if (hostElement && this.richTextShortcutCaptureHandler) {
      hostElement.removeEventListener('keydown', this.richTextShortcutCaptureHandler, {capture: true});
      this.richTextShortcutCaptureHandler = null;
    }

    if (this.richText && this.textAreaElement) {
      try {
        this.textAreaElement.destroy();
      } catch (error) {
        console.error(error);
      }
    }
  }
}
